import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { FastSvgComponent } from '@push-based/ngx-fast-svg';
import { TranslocoModule } from '@jsverse/transloco';

export type SiteDeviceCardType = 'gateway' | 'tracker' | 'antenna';

/**
 * Generic position card for the site configuration UI — drop-in replacement for the
 * inside `lib-device-card`. Re-exported via `domain-sites` so the integrator can use the
 * exact same look and feel without depending on `inside-sites`.
 *
 * <p>V8 design language: tight padding (matches the gateway/tracker cards in
 * other tabs), aquamarine ring when the position is set + grey otherwise,
 * dark-mode native via Tailwind {@code dark:} variants. Icons match the
 * site-view-map: {@code fast-svg gateway} / {@code tracker} for the
 * corresponding device kinds (antennas keep the index number badge — they're
 * positional, not a device-kind in the same sense).
 *
 * <p>Action buttons are now Tailwind-only (h-7 ghost buttons) so the card
 * sits flush at the same baseline as the rest of the V8 toolbar items, and
 * we don't drag in a PrimeNG-button shell that adds 8-12px of padding.
 */
@Component({
  selector: 'lib-site-device-card',
  standalone: true,
  imports: [FastSvgComponent, TranslocoModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="rounded-lg border px-3 py-2 transition-colors" [class]="cardSurfaceClass()">
      <!-- Top row: badge + name + actions. Smaller icon (size-7) + tighter
           gaps so the card lines up with the Preset / Map cards on the right
           column without dwarfing them. -->
      <div class="flex items-center gap-2.5">
        <div
          class="flex size-7 shrink-0 items-center justify-center rounded-md text-xs font-bold"
          [class]="iconBadgeClass()">
          @if (type() === 'antenna') {
            {{ index() }}
          } @else if (type() === 'gateway') {
            <fast-svg name="gateway" size="16" />
          } @else {
            <fast-svg name="tracker" size="16" />
          }
        </div>

        <div class="min-w-0 flex-1">
          <span class="block truncate text-sm font-semibold text-gray-900 dark:text-slate-100">{{ label() }}</span>
          @if (deviceId()) {
            <span class="block truncate text-xs text-gray-500 dark:text-slate-400">{{ deviceId() }}</span>
          }
        </div>

        <!-- Action icons row. {@code hidePosition} only suppresses the manual-position
             pin (gateway cards opt out of it because the position is implicit / handled
             by the draggable preview marker). The RTK button is gated by its own
             {@code showRtk} input — for 1-antenna gateways the RTK fix lands on the
             antenna position, which IS the gateway position, so the button must remain
             reachable even when the manual pin is hidden. -->
        @if (!hidePosition() || showRtk()) {
          <div class="flex shrink-0 gap-1">
            @if (!hidePosition()) {
              <button
                type="button"
                class="inline-flex size-7 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-50 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-100"
                [disabled]="disabled()"
                [title]="'siteDeviceCard.manualPosition' | transloco"
                (click)="manualPosition.emit()">
                <svg class="size-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path stroke-linecap="round" stroke-linejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>
            }
            @if (showRtk()) {
              <button
                type="button"
                class="text-pacificBlue-500 hover:bg-pacificBlue-50 dark:text-pacificBlue-400 dark:hover:bg-pacificBlue-500/15 inline-flex size-7 items-center justify-center rounded-md transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                [disabled]="disabled()"
                [title]="'siteDeviceCard.rtkPosition' | transloco"
                (click)="rtkPosition.emit()">
                <svg class="size-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
            }
          </div>
        }
      </div>

      <!-- Bottom row: position info. Pulled left to the badge edge ({@code pl-[38px]}
           to clear the size-7 badge + gap) so the coords sit under the name
           without a phantom indent. -->
      @if (!hidePosition()) {
        <div class="mt-1.5 pl-[38px]">
          @if (hasPosition()) {
            <span class="text-xs text-gray-600 dark:text-slate-400">
              {{ fmt(lat()) }}, {{ fmt(lng()) }}, {{ fmt(alt()) }}m
              @if (type() === 'tracker' && antennaAlt() !== null && antennaAlt() !== undefined) {
                · Antenna: {{ fmt(antennaAlt()) }}m
              }
            </span>
          } @else {
            <span class="text-xs font-medium text-red-500 dark:text-red-400">{{
              'siteDeviceCard.noPosition' | transloco
            }}</span>
          }
        </div>
      }
    </div>
  `,
})
export class SiteDeviceCardComponent {
  type = input.required<SiteDeviceCardType>();
  label = input.required<string>();
  deviceId = input<string>('');
  index = input<number>(0);
  disabled = input<boolean>(false);
  showRtk = input<boolean>(false);
  hidePosition = input<boolean>(false);

  lat = input<number | null | undefined>(undefined);
  lng = input<number | null | undefined>(undefined);
  alt = input<number | null | undefined>(undefined);
  antennaAlt = input<number | null | undefined>(undefined);

  manualPosition = output<void>();
  rtkPosition = output<void>();

  hasPosition(): boolean {
    const la = this.lat();
    const ln = this.lng();
    const al = this.alt();
    return la != null && ln != null && al != null && !isNaN(la) && !isNaN(ln) && !isNaN(al);
  }

  fmt(v: number | null | undefined): string {
    if (v == null || isNaN(v)) return '-';
    return v.toFixed(5);
  }

  /**
   * Returns the surface class string for the outer card. We can't use
   * {@code [class.…]} bindings for Tailwind utilities containing a slash
   * (e.g. {@code dark:bg-slate-900/40}) — Angular's template parser splits
   * on the slash and chokes. Using a single {@code [class]} binding with a
   * concatenated string works around that.
   */
  protected cardSurfaceClass(): string {
    if (!this.hidePosition() && this.hasPosition()) {
      return 'border-aquamarine-200 bg-aquamarine-50 dark:border-aquamarine-500/30 dark:bg-aquamarine-500/10';
    }
    return 'border-gray-200 bg-gray-50 dark:border-slate-700 dark:bg-slate-900/40';
  }

  /** Same constraint as {@link cardSurfaceClass} — slash-containing classes
   * forced into a single binding. */
  protected iconBadgeClass(): string {
    if (!this.hidePosition() && this.hasPosition()) {
      return 'bg-aquamarine-100 text-aquamarine-700 dark:bg-aquamarine-500/20 dark:text-aquamarine-300';
    }
    return 'bg-gray-200 text-gray-500 dark:bg-slate-700 dark:text-slate-400';
  }
}
